import { ReactElement, useMemo, useCallback } from 'react';
import { Stack } from '@mui/material';
import { SelectRowProps, TableChangeHandler } from 'react-bootstrap-table-next';

import {
  DataTable,
  DialogDetailsOrder,
  DialogChangeOrderState,
  DialogModifyAssignmentOrders,
  DialogSetFeedbackStop,
} from '~components/index';
import { useAppSelector, useExportTickets } from '~hooks/index';
import {
  useDataTableSelectedProps,
  SelectRowsHandlerOnSelectAll,
  SelectRowsHandlerOnSelect,
} from '~hooks/useDataTableSelectedProps';
import { selectAuth } from '~store/slices/auth/index';
import { ItemExtended } from '~services/item/types';
import { isOnlyReadUser } from '~utils/user';

import { useDashboardVariantV2CarrierContentInTransitTabContext } from '../../Context';

import { ITEM_KEY_ID } from '../../utils';
import { DashboardVariantV2CarrierContentDataTableContainer } from '../../../../styles';

import { useInTransitTabDataTableColumns } from './useColumns';
import { CUSTOM_SORT_FIELDS } from './customSortFields';

const DashboardVariantV2CarrierContentInTransitTabDataTable =
  (): ReactElement => {
    const { user } = useAppSelector(selectAuth);

    const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);

    const {
      parseFiltersRequest,
      selection: { selectedIds, utils: selectedIdsUtils },
      table: {
        data,
        loading: loadingRequestTable,
        totalSize,
        queryParams: { pagination },
        handleChangeTable: changeTable,
      },
      actions: { reloadDataAndResetSelectedRows },
      dialogs: {
        detailsOrder: {
          open: openDialogDetailsOrder,
          data: selectedDataForDetailsOrder,
          handleClose: handleCloseDialogDetailsOrder,
        },
        changeStateOrder: {
          open: openDialogChangeStateOrder,
          data: selectedOrderDataToChangeState,
          handleClose: handleCloseDialogChangeStateOrder,
        },
        modifyAssignmentOrder: {
          open: openDialogModifyAssignmentOrder,
          data: selectedItemIdToModifyAssignment,
          handleClose: handleCloseDialogModifyAssignmentOrder,
        },
        setFeedbackStop: {
          open: openDialogSetFeedbackStop,
          data: selectedItemSetFeedbackStop,
          handleClose: handleCloseDialogSetFeedbackStop,
        },
      },
    } = useDashboardVariantV2CarrierContentInTransitTabContext();

    const { loading: loadingExportTicket, onExportTickets: onExporTicket } =
      useExportTickets();

    const handleExportTicket = useCallback(
      async (trackingCode: string) => {
        await onExporTicket({ trackingCodes: [trackingCode] });
      },
      [onExporTicket],
    );

    const columns = useInTransitTabDataTableColumns({
      loadingExportTicket,
      onExporTicket: handleExportTicket,
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleChangeTable = useCallback<TableChangeHandler<any>>(
      (type, newState) => {
        const sortField =
          CUSTOM_SORT_FIELDS[newState?.sortField] ?? newState?.sortField;

        changeTable(type, {
          pagination: { ...newState, sortField },
          filters: parseFiltersRequest(),
        });
      },
      [changeTable, parseFiltersRequest],
    );

    const handleOnSelectAll = useCallback<
      SelectRowsHandlerOnSelectAll<ItemExtended>
    >(
      (isSelected, rows) => {
        if (isSelected) {
          const ids = rows.map((row) => row[ITEM_KEY_ID]);
          selectedIdsUtils.addMultiple(ids);
        } else {
          selectedIdsUtils.reset();
        }
      },
      [selectedIdsUtils],
    );

    const handleOnSelect = useCallback<SelectRowsHandlerOnSelect<ItemExtended>>(
      (row) => {
        const currentId = row[ITEM_KEY_ID];
        selectedIdsUtils.toggle(currentId);
      },
      [selectedIdsUtils],
    );

    const selectRowsProps = useDataTableSelectedProps({
      onSelectAll: handleOnSelectAll,
      onSelect: handleOnSelect,
      selectedIds,
    });

    const dataTableSelectRowProps = useMemo<
      SelectRowProps<ItemExtended> | undefined
    >(
      () => (isUserOnlyReader ? undefined : selectRowsProps),
      [isUserOnlyReader, selectRowsProps],
    );

    const handleSuccessChangeOrdersState = useCallback(() => {
      handleCloseDialogChangeStateOrder();

      reloadDataAndResetSelectedRows();
    }, [reloadDataAndResetSelectedRows, handleCloseDialogChangeStateOrder]);

    return (
      <>
        <Stack>
          <DashboardVariantV2CarrierContentDataTableContainer fullWidth>
            <DataTable
              loading={loadingRequestTable}
              data={data}
              columns={columns}
              keyField={ITEM_KEY_ID}
              pagination={{
                options: {
                  sizePerPage: pagination.sizePerPage,
                  page: pagination.page,
                  totalSize,
                },
              }}
              remote={{
                pagination: true,
              }}
              onTableChange={handleChangeTable}
              selectRow={dataTableSelectRowProps}
              hideableStorageKey="Dashboard-Carrier-In-Transit-Data-Table"
              toggleHideableColumns
              excludeHideableFieldColumns={['title', 'itemStateTypeId']}
            />
          </DashboardVariantV2CarrierContentDataTableContainer>
        </Stack>

        {openDialogDetailsOrder && selectedDataForDetailsOrder && (
          <DialogDetailsOrder
            open={openDialogDetailsOrder}
            onClose={handleCloseDialogDetailsOrder}
            enabledChipTitle
            {...selectedDataForDetailsOrder}
          />
        )}

        {openDialogChangeStateOrder && selectedOrderDataToChangeState && (
          <DialogChangeOrderState
            open={openDialogChangeStateOrder}
            onClose={handleCloseDialogChangeStateOrder}
            {...selectedOrderDataToChangeState}
            onChangeSuccess={handleSuccessChangeOrdersState}
          />
        )}

        {openDialogModifyAssignmentOrder &&
          selectedItemIdToModifyAssignment && (
            <DialogModifyAssignmentOrders
              open={openDialogModifyAssignmentOrder}
              onClose={() => handleCloseDialogModifyAssignmentOrder()}
              itemIds={[selectedItemIdToModifyAssignment]}
              onSuccessModify={() => reloadDataAndResetSelectedRows()}
            />
          )}

        {openDialogSetFeedbackStop && selectedItemSetFeedbackStop && (
          <DialogSetFeedbackStop
            open={openDialogSetFeedbackStop}
            onClose={handleCloseDialogSetFeedbackStop}
            routeItemId={selectedItemSetFeedbackStop.routeItemId ?? ''}
            routeId={selectedItemSetFeedbackStop.routeId ?? ''}
            itemId={selectedItemSetFeedbackStop.itemId}
          />
        )}
      </>
    );
  };

export default DashboardVariantV2CarrierContentInTransitTabDataTable;
